import React, {useState} from "react";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {GridTable, ResultsPager} from "../../../../Components/Grid";
import {LoanLibraryLoanRow} from "./LoanLibraryLoanRow";
import {copyToClipboard} from "./loanLibraryLoanUtility";
import {LoanLibraryLoanEditFormView} from "./LoanLibraryLoanEditFormView";
import {DialogBox} from "../../../../Components/Dialog/DialogBox";
import {isNullOrUndefined} from "../../../../Utilities/commonUtilities";
import {LoanLibraryLoanItem, LoanLibraryLoanSearchCriteria} from "./loanLibraryLoan.types";

interface LoanLibraryLoanTableProps {
    canManageLoanLibraryLoans: boolean;
    handleSaveUpdatedLoanLibraryLoanItem: (item: LoanLibraryLoanItem) => void;
    handleSetLoanLibraryLoanSearchCriteria: (criteria: LoanLibraryLoanSearchCriteria) => void;
    loanLibraryLoanItems: LoanLibraryLoanItem[];
    loanLibraryLoanSearchCriteria: LoanLibraryLoanSearchCriteria;
    updateLoanLibraryLoanItemInState: (loanId: string, status: string | number, notes: string | null, numberOfExtensionDays: number | null) => void;
    handleReturnLoan: (loanId: string) => void;
    totalRecords: number;
}

export const LoanLibraryLoanTable = ({
                                         canManageLoanLibraryLoans,
                                         handleSaveUpdatedLoanLibraryLoanItem,
                                         handleSetLoanLibraryLoanSearchCriteria,
                                         loanLibraryLoanItems,
                                         loanLibraryLoanSearchCriteria,
                                         updateLoanLibraryLoanItemInState,
                                         handleReturnLoan,
                                         totalRecords,
                                     }: LoanLibraryLoanTableProps) => {

    const [loanLibraryLoanItemForDialog, setLoanLibraryLoanItemForDialog] = useState<LoanLibraryLoanItem | null>(null);
    const [isLoanLibraryLoanEditDialogOpen, setIsLoanLibraryLoanEditDialogOpen] = useState(false);

    const handleFirstClick = () => {
        let criteria = {...loanLibraryLoanSearchCriteria};
        criteria.First();
        criteria.loadSearchResults = true;
        handleSetLoanLibraryLoanSearchCriteria(criteria);
    };

    const handlePreviousClick = () => {
        let criteria = {...loanLibraryLoanSearchCriteria};
        criteria.Previous();
        criteria.loadSearchResults = true;
        handleSetLoanLibraryLoanSearchCriteria(criteria);
    };

    const handleNextClick = () => {
        let criteria = {...loanLibraryLoanSearchCriteria};
        criteria.Next();
        criteria.loadSearchResults = true;
        handleSetLoanLibraryLoanSearchCriteria(criteria);
    };

    const handleLastClick = () => {
        let criteria = {...loanLibraryLoanSearchCriteria};
        criteria.Last(totalRecords);
        criteria.loadSearchResults = true;
        handleSetLoanLibraryLoanSearchCriteria(criteria);
    };

    const pager = (isSecondaryPager: boolean) =>
        <ResultsPager
            handleFirstClick={handleFirstClick}
            handlePreviousClick={handlePreviousClick}
            handleNextClick={handleNextClick}
            handleLastClick={handleLastClick}
            isSecondaryPager={isSecondaryPager}
            totalRecords={totalRecords}
            recordsPerPage={loanLibraryLoanSearchCriteria.recordsPerPage}
            currentPage={loanLibraryLoanSearchCriteria.page}
            disabled={false}
        />;

    const handleOrderClick = (event: React.MouseEvent<HTMLElement>) => {
        let criteria = {...loanLibraryLoanSearchCriteria};
        const columnId = (event.target as HTMLElement).dataset.id;
        if (columnId) criteria.UpdateSorting(columnId);
        criteria.loadSearchResults = true;
        handleSetLoanLibraryLoanSearchCriteria(criteria);
    };

    const handleOpenEditDialog = (event: React.MouseEvent<HTMLElement>, loanLibraryLoanItem: LoanLibraryLoanItem) => {
        event.preventDefault();

        setLoanLibraryLoanItemForDialog({
            ...loanLibraryLoanItem,
            numberOfExtensionDays: loanLibraryLoanItem.numberOfExtensionDays ?? null,
        });
        setIsLoanLibraryLoanEditDialogOpen(true);
    }

    const handleCloseEditDialog = () => setIsLoanLibraryLoanEditDialogOpen(false);


    const handleSaveLoanLibraryLoanItem = () => {
        if (loanLibraryLoanItemForDialog) {
            handleSaveUpdatedLoanLibraryLoanItem(loanLibraryLoanItemForDialog);
            handleCloseEditDialog();
        }
    };

    const handleUpdateLoanStatus = (loanLibraryLoanId: string, loanLibraryLoanStatus: string | number) => {
        updateLoanLibraryLoanItemInState(loanLibraryLoanId, loanLibraryLoanStatus, null, null);
    }


    const isLoanLibraryLoanItemsEmpty = isArrayNullOrEmpty(loanLibraryLoanItems);
    const canOpenLoanLibraryLoanItemEditDialog = canManageLoanLibraryLoans && isLoanLibraryLoanEditDialogOpen && loanLibraryLoanItemForDialog;

    return <>
        {pager(false)}
        <GridTable className="searchResults"  style={{tableLayout: 'fixed'}}>
            <thead>
            <tr>
                <th onClick={handleOrderClick} className="is-clickable" data-id="inventoryShortId">Item ID</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="dateCheckedOut">Checked Out Date</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="dateDue">Due Date</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="dateSubmitted">Date Submitted</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="catalogItemTitle">Title</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="patronName">Patron</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="patronEmail">Patron Email</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="shelfLocation">Shelf Location
                </th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="shippingLocationName">Shipping Location
                </th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="isSchoolAddress">Is School Address</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="status">Status</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="notes">Notes</th>
                <th scope={"col"} className={"align-center"}>Actions</th>
            </tr>
            </thead>
            <tbody>
            {
                !isLoanLibraryLoanItemsEmpty &&
                loanLibraryLoanItems.map((loanLibraryLoanItem) => {
                    const handleCopyToClipboard = async () => {
                        const shippingLocationDetails = loanLibraryLoanItem.shippingLocation;
                        const addressToCopy = `${shippingLocationDetails.name}\n${shippingLocationDetails.address}\n${!isNullOrUndefined(shippingLocationDetails.address2) ? shippingLocationDetails.address2.trim() + '\n' : ''}${shippingLocationDetails.city}, ${shippingLocationDetails.state} ${shippingLocationDetails.zipCode}`;

                        await copyToClipboard(addressToCopy);
                    };

                    return <LoanLibraryLoanRow
                        key={loanLibraryLoanItem.loanLibraryLoanId}
                        loanLibraryLoanItem={loanLibraryLoanItem}
                        canManageLoanLibraryLoans={canManageLoanLibraryLoans}
                        handleCopyToClipboard={handleCopyToClipboard}
                        handleOpenEditDialog={handleOpenEditDialog}
                        handleUpdateLoanStatus={handleUpdateLoanStatus}
                        handleReturnLoan={handleReturnLoan}
                    />;
                })
            }
            {
                isLoanLibraryLoanItemsEmpty &&
                <tr data-testid={`no-loan-row`}>
                    <td colSpan={13} className={`text-center`}>No results found.</td>
                </tr>
            }
            </tbody>
        </GridTable>
        {pager(true)}
        {
            canOpenLoanLibraryLoanItemEditDialog &&
            <DialogBox
                handleCancel={handleCloseEditDialog}
                handleSave={handleSaveLoanLibraryLoanItem}
                openDialog={isLoanLibraryLoanEditDialogOpen}
                title={`Edit Loan Library Loan Item: ${loanLibraryLoanItemForDialog.catalogItemTitle}`}>
                <LoanLibraryLoanEditFormView
                    loanLibraryLoanItemForDialog={loanLibraryLoanItemForDialog}
                    setLoanLibraryLoanItemForDialog={setLoanLibraryLoanItemForDialog}/>
            </DialogBox>
        }
    </>;
};

